/**
 * VitaWoof PDP — mobile gallery thumbnails.
 *
 * GOAL 2: The Horizon theme's slideshow_mobile_controls_style has no 'thumbnails'
 * option. This script builds a real thumbnail strip below the mobile gallery
 * by reading slide images from the <media-gallery> element.
 *
 * Works with Horizon's carousel: slides are <li> inside a scroll-snap container.
 * Each slide has either an <img> or a <video poster>.
 * Clicking a thumb scrolls the slide into view via scrollIntoView / scrollTo.
 *
 * FIX 2 (regression fix): the strip is inserted AFTER the
 * .product-information__media wrapper — that wrapper is capped at 34vh with
 * overflow:hidden on mobile, so anything inside it (including a strip inserted
 * after <media-gallery>, which is still inside the wrapper) gets clipped.
 * Outside the wrapper the strip renders visibly between gallery and title.
 * Fallback: insert after media-gallery if the wrapper isn't found.
 *
 * Re-attaches after variant:update because media-gallery does replaceWith()
 * (confirmed in findings: connectedCallback re-runs on the new instance).
 * Because the strip now lives OUTSIDE the replaced media-gallery subtree it
 * survives the replaceWith — so before inserting we remove any stale strip
 * sibling to avoid duplicates (the new gallery has no data-vw-thumbs-built
 * guard, so buildThumbs runs again and rebuilds cleanly).
 */

const THUMB_ATTR = 'data-vw-thumbs-built';

function buildThumbs(gallery) {
  if (!gallery || gallery.hasAttribute(THUMB_ATTR)) return;
  gallery.setAttribute(THUMB_ATTR, '1');

  // Only run on mobile viewport.
  if (window.matchMedia('(min-width: 750px)').matches) return;

  // Find the scroll container (Horizon uses a <ul> with scroll-snap).
  const scroller = gallery.querySelector('.media-gallery__scroll-area, [data-media-gallery-scroll], ul.scroll-snap')
    || gallery.querySelector('ul');
  if (!scroller) return;

  const slides = Array.from(scroller.querySelectorAll(':scope > li, :scope > [data-media-id]'));
  if (slides.length < 2) return; // No strip needed for single media.

  const thumbs = slides.map((slide, i) => {
    const img = slide.querySelector('img');
    const video = slide.querySelector('video');
    const src = (img && (img.dataset.src || img.src)) || (video && video.getAttribute('poster')) || '';
    const btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'vw-thumbs__btn';
    btn.setAttribute('aria-label', `Bild ${i + 1}`);
    btn.dataset.index = String(i);
    if (src) {
      // Use a small version: Shopify CDN supports ?width= param.
      const smallSrc = src.includes('cdn.shopify.com') ? src.replace(/\.jpg(\?.*)?$/, '.jpg').split('?')[0] + '?width=120' : src;
      btn.style.backgroundImage = `url(${smallSrc})`;
    } else {
      btn.style.background = 'rgba(27,58,47,0.08)';
    }
    return btn;
  });

  const strip = document.createElement('div');
  strip.className = 'vw-thumbs';
  strip.setAttribute('role', 'tablist');
  strip.setAttribute('aria-label', 'Bilder');
  thumbs.forEach((btn) => strip.appendChild(btn));

  // FIX 2: insert the strip AFTER the .product-information__media wrapper
  // (34vh + overflow:hidden on mobile would clip anything inside it).
  // Fall back to media-gallery afterend if the wrapper isn't found.
  const wrapper = gallery.closest('.product-information__media');
  const anchor = wrapper || gallery;

  // Remove any stale strip from a previous gallery instance (the strip lives
  // outside the replaced media-gallery subtree, so it survives replaceWith).
  const parent = anchor.parentElement;
  if (parent) {
    parent.querySelectorAll(':scope > .vw-thumbs').forEach((el) => el.remove());
  }

  anchor.insertAdjacentElement('afterend', strip);

  // Track active slide.
  function setActive(index) {
    thumbs.forEach((btn, i) => {
      const on = i === index;
      btn.classList.toggle('is-on', on);
      btn.setAttribute('aria-selected', on ? 'true' : 'false');
    });
  }
  setActive(0);

  // Click: scroll the corresponding slide into view.
  strip.addEventListener('click', (e) => {
    const btn = e.target.closest('.vw-thumbs__btn');
    if (!btn) return;
    const idx = parseInt(btn.dataset.index, 10);
    const slide = slides[idx];
    if (!slide) return;
    // Prefer Horizon's own slide navigation if it exposes it.
    const picker = gallery.querySelector('media-gallery-picker, slideshow-component');
    if (picker && typeof picker.goToSlide === 'function') {
      picker.goToSlide(idx);
    } else {
      slide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
    }
    setActive(idx);
  });

  // IntersectionObserver to sync active thumb as user swipes.
  if ('IntersectionObserver' in window) {
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          const idx = slides.indexOf(entry.target);
          if (idx !== -1) setActive(idx);
        }
      });
    }, { root: scroller, threshold: 0.55 });
    slides.forEach((slide) => obs.observe(slide));
    // Clean up when gallery is removed.
    gallery.addEventListener('disconnectedCallback', () => obs.disconnect(), { once: true });
  }
}

function initThumbs() {
  // Build for any already-present gallery.
  document.querySelectorAll('media-gallery').forEach(buildThumbs);

  // Re-build after variant changes (media-gallery does replaceWith — new element
  // without the guard attribute; buildThumbs removes the stale strip first).
  document.addEventListener('variant:update', () => {
    // Small delay to let the new gallery settle in the DOM.
    requestAnimationFrame(() => {
      document.querySelectorAll('media-gallery').forEach(buildThumbs);
    });
  });

  // Also handle MutationObserver for robustness.
  const mo = new MutationObserver((mutations) => {
    for (const m of mutations) {
      m.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.tagName && node.tagName.toLowerCase() === 'media-gallery') buildThumbs(node);
        node.querySelectorAll && node.querySelectorAll('media-gallery').forEach(buildThumbs);
      });
    }
  });
  mo.observe(document.body, { childList: true, subtree: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initThumbs);
} else {
  initThumbs();
}
